import { v4 as uuidv4 } from 'uuid';
import { Base_URL } from '../Client/apiURL';

const ALLOWED_EVENTS = new Set([
  'page_view', 'dashboard_view', 'results_view', 'test_list_viewed',
  'practice_question_opened', 'test_started', 'test_section_changed',
  'test_submitted', 'subscription_page_viewed', 'plan_selected',
  'checkout_started', 'promo_applied', 'bank_transfer_started',
  'bank_transfer_submitted', 'payment_status_viewed', 'card_payment_failed',
  'client_error', 'active_time', 'test_abandoned', 'test_resumed',
  'promo_failed', 'checkout_abandoned', 'api_error', 'audio_error',
  'scoring_error', 'page_performance',
  'phone_prompt_shown', 'phone_submit_attempted', 'phone_submit_succeeded',
  'phone_submit_failed', 'phone_prompt_exited',
]);
const FORBIDDEN_KEY = /(password|passcode|token|secret|authorization|cookie|email|phone|contact|card|bank|account|answer|audio|transcript|user_response|request_body|response_body|headers?|storage|credential|^user_id$|^portal_id$)/i;
const EMAIL_VALUE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_VALUE = /^\+?[\d\s().-]{7,}$/;
const BEARER_IN_TEXT = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
const NAMED_SECRET_IN_TEXT = /\b(password|passcode|token|secret|api[_-]?key|access[_-]?token|refresh[_-]?token|credential)\s*([:=])\s*["']?[^&\s"',}\]]+/gi;
const AWS_KEY_IN_TEXT = /\b(?:AKIA|ASIA)[A-Z0-9]{16}\b/g;
const PRIVATE_KEY_IN_TEXT = /\b(?:sk-[A-Za-z0-9_-]{12,}|(?:sk|rk|pk)_(?:live|test)_[A-Za-z0-9_-]{8,})\b/g;

function sanitizeString(value) {
  const trimmed = String(value).trim();
  if (EMAIL_VALUE.test(trimmed) || PHONE_VALUE.test(trimmed)) return undefined;
  return trimmed
    .replace(BEARER_IN_TEXT, 'Bearer [masked]')
    .replace(NAMED_SECRET_IN_TEXT, '$1$2[masked]')
    .replace(AWS_KEY_IN_TEXT, '[masked]')
    .replace(PRIVATE_KEY_IN_TEXT, '[masked]')
    .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '[masked]')
    .replace(/\+?[\d][\d\s().-]{6,}[\d]/g, '[masked]')
    .slice(0, 500);
}

function sanitizeProperties(properties = {}, depth = 0) {
  if (!properties || typeof properties !== 'object' || depth > 3) return {};
  return Object.entries(properties).slice(0, 40).reduce((safe, [key, value]) => {
    if (FORBIDDEN_KEY.test(key)) return safe;
    if (typeof value === 'string') {
      const sanitized = sanitizeString(value);
      if (sanitized !== undefined) safe[key] = sanitized;
    }
    else if (typeof value === 'number' && Number.isFinite(value)) safe[key] = value;
    else if (typeof value === 'boolean' || value === null) safe[key] = value;
    else if (Array.isArray(value)) safe[key] = value.slice(0, 20).map((item) => {
      if (typeof item === 'string') return sanitizeString(item);
      if (typeof item === 'object') return sanitizeProperties(item, depth + 1);
      return item;
    }).filter((item) => item !== undefined);
    else if (typeof value === 'object') safe[key] = sanitizeProperties(value, depth + 1);
    return safe;
  }, {});
}

export function createAnalyticsClient({
  endpoint = `${Base_URL}/app/users/analytics/events`,
  transport = (url, options) => fetch(url, options),
  uuid = uuidv4,
  flushIntervalMs = 3000,
  maxQueueSize = 500,
  autoStart = true,
} = {}) {
  const queue = [];
  let sessionId = uuid();
  let enabled = false;
  let failedBatches = 0;
  let flushPromise = null;
  let timer = null;
  let backoffUntil = 0;

  function setEnabled(nextEnabled) {
    enabled = Boolean(nextEnabled);
  }

  function resetIdentity() {
    queue.length = 0;
    sessionId = uuid();
    failedBatches = 0;
    backoffUntil = 0;
  }

  function removeSentEvents(sentEvents) {
    for (const sent of sentEvents) {
      const index = queue.indexOf(sent);
      if (index !== -1) queue.splice(index, 1);
    }
  }

  function track(eventName, { product = null, path = window.location.pathname, properties = {} } = {}) {
    if (!enabled || !ALLOWED_EVENTS.has(eventName) || queue.length >= maxQueueSize) return false;
    queue.push({
      event_id: uuid(),
      event_name: eventName,
      event_time: new Date().toISOString(),
      session_id: sessionId,
      product: ['pte', 'ielts', 'general', 'word_quest'].includes(product) ? product : null,
      path: String(path || '/').split(/[?#]/)[0].slice(0, 500),
      properties: sanitizeProperties(properties),
    });
    return true;
  }

  async function flush() {
    if (flushPromise) return flushPromise;
    if (!enabled || queue.length === 0) return 0;
    if (Date.now() < backoffUntil) return 0;
    const batchSize = Math.min(50, queue.length);
    const batch = queue.slice(0, batchSize);
    flushPromise = Promise.resolve(transport(endpoint, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    }))
      .then((response) => {
        if (response && !response.ok && response.status >= 400) {
          failedBatches += 1;
          backoffUntil = Date.now() + Math.min(30000, Math.pow(2, Math.min(failedBatches, 8)) * 1000);
          return 0;
        }
        backoffUntil = 0;
        removeSentEvents(batch);
        return batch.length;
      })
      .catch(() => {
        failedBatches += 1;
        backoffUntil = Date.now() + Math.min(30000, Math.pow(2, Math.min(failedBatches, 8)) * 1000);
        return 0;
      })
      .finally(() => { flushPromise = null; });
    return flushPromise;
  }

  function flushWithBeacon(beacon = typeof navigator !== 'undefined' && navigator.sendBeacon
    ? navigator.sendBeacon.bind(navigator)
    : null) {
    if (!enabled || queue.length === 0 || !beacon) return 0;
    const batch = queue.splice(0, 50);
    try {
      const sent = beacon(endpoint, new Blob(
        [JSON.stringify({ events: batch })],
        { type: 'application/json' }
      ));
      if (!sent) {
        queue.unshift(...batch);
        return 0;
      }
      return batch.length;
    } catch (_) {
      queue.unshift(...batch);
      return 0;
    }
  }

  function handlePageHide() {
    if (!flushWithBeacon()) flush();
  }

  function start() {
    if (!timer) {
      timer = window.setInterval(flush, flushIntervalMs);
      window.addEventListener('pagehide', handlePageHide);
    }
  }

  function stop() {
    if (timer) {
      window.clearInterval(timer);
      window.removeEventListener('pagehide', handlePageHide);
    }
    timer = null;
  }

  function getHealth() {
    return { queued: queue.length, failed_batches: failedBatches };
  }

  function getSessionId() {
    return sessionId;
  }

  if (autoStart && typeof window !== 'undefined') start();

  return { setEnabled, resetIdentity, track, flush, flushWithBeacon, start, stop, getHealth, getSessionId };
}

export const analyticsClient = createAnalyticsClient();
